import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, FileText, Trash2, Inbox, Paperclip, ExternalLink, RefreshCw } from "lucide-react";

/**
 * Iter-179: Sandbox Inbox + Drive Vault — shows every fake email and PDF that was
 * generated from the sandbox environment. Useful for demoing the platform without
 * sending real emails or uploading to real Drive.
 *
 * Visible to sandbox users (their own outbox/vault) and to the real PROD CEO (all rows).
 */
export default function SandboxInbox() {
  const [emails, setEmails] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("emails");
  const [openEmail, setOpenEmail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [e, f] = await Promise.all([api.get("/sandbox/emails"), api.get("/sandbox/pdfs")]);
      setEmails(e.data.emails || []);
      setFiles(f.data.files || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const purge = async () => {
    if (!window.confirm("Clear all simulated emails and PDFs from the sandbox vault?")) return;
    try {
      const r = await api.post("/sandbox/inbox/purge");
      toast.success(`Purged ${r.data.emails_deleted} email(s) and ${r.data.files_deleted} file(s).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filePreviewUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/sandbox/drive/${id}/preview`;

  return (
    <div className="space-y-6" data-testid="sandbox-inbox-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-orange font-mono">SANDBOX</div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mt-1 leading-tight">Sandbox Inbox & Vault</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Every email sent and every PDF generated from the sandbox lives here, NOT on Google. Nothing reaches real recipients.
            Use Purge to clean the slate after a demo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="sandbox-inbox-refresh"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-300 bg-white text-zinc-700 text-xs font-medium rounded-md hover:bg-zinc-50"
          ><RefreshCw size={13} /> Refresh</button>
          <button
            onClick={purge}
            data-testid="sandbox-inbox-purge"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-red-300 bg-red-50 text-red-700 text-xs font-medium rounded-md hover:bg-red-100"
          ><Trash2 size={13} /> Purge all</button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-zinc-200">
        <button
          onClick={() => setTab("emails")}
          data-testid="sandbox-tab-emails"
          className={`relative -mb-px px-4 py-2 text-sm font-medium ${tab === "emails" ? "border-b-2 border-brand-navy text-brand-navy" : "text-zinc-500"}`}
        ><Mail size={13} className="inline mr-1.5" /> Emails ({emails.length})</button>
        <button
          onClick={() => setTab("files")}
          data-testid="sandbox-tab-files"
          className={`relative -mb-px px-4 py-2 text-sm font-medium ${tab === "files" ? "border-b-2 border-brand-navy text-brand-navy" : "text-zinc-500"}`}
        ><FileText size={13} className="inline mr-1.5" /> PDFs & files ({files.length})</button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 p-8 text-center">Loading…</div>
      ) : tab === "emails" ? (
        <EmailList emails={emails} onOpen={setOpenEmail} />
      ) : (
        <FilesList files={files} previewUrl={filePreviewUrl} />
      )}

      {openEmail && <EmailPreview email={openEmail} onClose={() => setOpenEmail(null)} />}
    </div>
  );
}

function EmailList({ emails, onOpen }) {
  if (emails.length === 0) {
    return <EmptyState icon={<Inbox size={32} className="text-zinc-300" />} title="No simulated emails yet" hint="Generate an invoice, send a Weekly CRA, or rely on auto-invoice from the sandbox to see entries appear here." />;
  }
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      {emails.map((e) => (
        <button
          key={e.sandbox_email_id}
          onClick={() => onOpen(e)}
          data-testid={`sb-email-${e.sandbox_email_id}`}
          className="w-full text-left flex flex-col gap-1 md:flex-row md:items-center md:gap-4 px-4 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
        >
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-zinc-900 truncate">{e.subject || "(no subject)"}</div>
            <div className="text-xs text-zinc-500 truncate">To: {e.to}{e.cc ? ` · Cc: ${e.cc}` : ""}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0 text-[11px] font-mono text-zinc-500">
            {(e.attachments && e.attachments.length > 0) && <span className="flex items-center gap-1"><Paperclip size={11} />{e.attachments.length}</span>}
            <span>{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function FilesList({ files, previewUrl }) {
  if (files.length === 0) {
    return <EmptyState icon={<FileText size={32} className="text-zinc-300" />} title="No simulated PDFs yet" hint="Generate a Weekly CRA, an invoice, or a Monthly CRA from a sandbox profile to see files appear here." />;
  }
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      {files.map((f) => (
        <div
          key={f.sandbox_drive_file_id}
          data-testid={`sb-file-${f.sandbox_drive_file_id}`}
          className="flex flex-col gap-1 md:flex-row md:items-center md:gap-4 px-4 py-3 border-b border-zinc-100 last:border-b-0"
        >
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-zinc-900 truncate">{f.filename}</div>
            <div className="text-xs text-zinc-500 truncate">{f.mime_type} · {Math.round((f.size_bytes || 0) / 1024)} KB</div>
          </div>
          <a
            href={previewUrl(f.sandbox_drive_file_id)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-3 h-8 bg-brand-navy text-white rounded-md text-xs font-medium hover:bg-brand-navy-600 shrink-0"
            data-testid={`sb-file-preview-${f.sandbox_drive_file_id}`}
          ><ExternalLink size={12} /> Preview</a>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ icon, title, hint }) {
  return (
    <div className="bg-white border border-zinc-200 border-dashed rounded-lg p-10 text-center">
      <div className="flex justify-center mb-3">{icon}</div>
      <div className="text-sm font-medium text-zinc-700">{title}</div>
      <div className="text-xs text-zinc-500 mt-1.5 max-w-md mx-auto">{hint}</div>
    </div>
  );
}

function EmailPreview({ email, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="sb-email-preview"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-lg md:rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <div className="px-5 py-4 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Sandbox · simulated</div>
          <div className="font-medium text-lg mt-1">{email.subject}</div>
          <div className="text-xs text-zinc-600 mt-1">
            From: {email.from_name || email.from_email} &lt;{email.from_email}&gt;<br />
            To: {email.to}{email.cc ? <><br />Cc: {email.cc}</> : null}<br />
            {(email.created_at || "").slice(0, 19).replace("T", " ")}
          </div>
        </div>
        <div className="px-5 py-4">
          {email.body_html ? (
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: email.body_html }} />
          ) : (
            <pre className="text-xs whitespace-pre-wrap font-mono">{email.body_text}</pre>
          )}
          {email.attachments && email.attachments.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-200">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono mb-2">Attachments</div>
              <ul className="text-sm space-y-1">
                {email.attachments.map((a, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Paperclip size={12} /> {a.filename} ({Math.round((a.size_bytes || 0) / 1024)} KB)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 text-right">
          <button onClick={onClose} className="px-4 h-9 bg-zinc-100 text-zinc-700 rounded-md text-sm font-medium hover:bg-zinc-200" data-testid="sb-email-close">Close</button>
        </div>
      </div>
    </div>
  );
}
